'use client';

import { AdvancedFilter } from '@cldsystems/e3-fe-lib';
import {
  useCompanyGetById,
  usePriceListGetList,
  WebshopPriceList,
} from '@cldsystems/ws-api-client';
import { cva, VariantProps } from 'class-variance-authority';
import { FC } from 'react';
import VatPriceSuffix, {
  type VatPriceSuffixSize,
} from '@/components/common/vat-price-suffix/vat-price-suffix';
import useCoreConfig from '@/hooks/use-core-config';
import { useKeycloak } from '@/providers/keycloak-provider';
import { useStore } from '@/store/zustand/zustand.setup';
import { cn } from '@/utils';
import { formatPriceForDisplay } from '@/utils/price-check';

const priceIntegral = cva('', {
  variants: {
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
    },
    color: {
      red: 'text-danger-600',
      black: 'text-foreground',
      success: 'text-success',
      gray: 'text-gray-500',
      white: 'text-white',
      whiteMuted: 'text-white/70',
    },
    lineThrough: {
      true: 'line-through',
    },
    fontWeight: {
      normal: 'font-medium',
      bold: 'font-bold',
    },
  },
  defaultVariants: {
    size: 'md',
    color: 'black',
    fontWeight: 'normal',
  },
});

const priceCurrency = cva('ml-1', {
  variants: {
    size: {
      xs: 'text-xxs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
    },
    color: {
      red: 'text-danger-600',
      black: 'text-foreground',
      success: 'text-success',
      gray: 'text-gray-500',
      white: 'text-white',
      whiteMuted: 'text-white/70',
    },
    fontWeight: {
      normal: 'font-normal',
      bold: 'font-bold',
    },
    lineThrough: {
      true: 'line-through',
    },
  },
  defaultVariants: {
    size: 'md',
    color: 'black',
    fontWeight: 'normal',
  },
});

type Props = {
  price: number | undefined;
  isVerticalDirection?: boolean;
  lineThrough?: boolean;
  categoryId?: string;
  productId?: string;
  /** When true, appends ex-VAT label after currency when system config marks prices as ex-VAT. */
  showVatSuffix?: boolean;
  /** Overrides VAT label size; defaults to the same `size` as the price. */
  vatSuffixSize?: VatPriceSuffixSize;
  /** Overrides the price color with an arbitrary CSS color (e.g. hex from studio theme resolver). */
  customColor?: string;
  /** Passed to VatPriceSuffix as custom text color. */
  vatSuffixColor?: string;
  /** Passed to VatPriceSuffix as custom background color. */
  vatSuffixBackground?: string;
} & VariantProps<typeof priceIntegral>;

const FormattedPrice: FC<Props> = ({
  price,
  size,
  color,
  fontWeight,
  lineThrough,
  categoryId,
  productId,
  showVatSuffix,
  vatSuffixSize,
  customColor,
  vatSuffixColor,
  vatSuffixBackground,
  isVerticalDirection = false,
}) => {
  const { currency } = useCoreConfig();
  const { keycloak } = useKeycloak();
  const companyId = useStore(state => state.company.companyId);
  const isAuthenticatedWithCompany = Boolean(keycloak?.authenticated) && Boolean(companyId);

  const advancedFilter: AdvancedFilter<WebshopPriceList> = {
    AND: [
      {
        OR: [
          {
            'companies.id': {
              operator: 'contains',
              value: companyId,
            },
          },
        ],
      },
      {
        stage: {
          operator: 'not_equals',
          value: 'Inactive',
        },
      },
    ],
  };

  const { data: priceList } = usePriceListGetList({
    params: { from: '0', size: '100' },
    queries: { advancedFilter: JSON.stringify(advancedFilter) },
    enabled: isAuthenticatedWithCompany,
    staleTime: Infinity,
  });

  const productPriceFromPriceList = priceList?.items[0]?.products?.find(
    ({ id }) => id === productId,
  );

  const { data: company } = useCompanyGetById({
    params: { id: companyId ?? '' },
    enabled: isAuthenticatedWithCompany,
    staleTime: Infinity,
  });

  if (price === undefined) return;

  let finalPrice: number = 0;

  if (productPriceFromPriceList) {
    // Price-list entries can carry a `discountedPrice < price` to model a
    // promo on top of the negotiated list price. We pick which side of the
    // pair to render based on the visual `lineThrough` flag — the consumer
    // renders TWO `FormattedPrice` instances for a discount (one struck
    // through, one in red), and `lineThrough` is the natural signal for the
    // "old / list" price vs. the "current / discounted" price. When the
    // price-list does not define a real promo, we fall back to the list
    // price for both calls, preserving the prior behaviour.
    const hasPriceListDiscount =
      productPriceFromPriceList.discountedPrice > 0 &&
      productPriceFromPriceList.discountedPrice < productPriceFromPriceList.price;
    finalPrice =
      hasPriceListDiscount && !lineThrough
        ? productPriceFromPriceList.discountedPrice
        : productPriceFromPriceList.price;
  } else {
    const categoryDiscount = company?.company.categoryDiscounts?.find(
      ({ id }) => id === categoryId,
    );

    const discount = categoryDiscount?.discount ?? 0;

    finalPrice = discount !== 0 ? price - price * (discount / 100) : price;
  }

  const effectiveSize = (size ?? 'md') as VatPriceSuffixSize;
  const effectiveVatSize = vatSuffixSize ?? effectiveSize;

  return (
    <span
      className={cn(
        'inline-flex flex-wrap items-baseline gap-x-1',
        isVerticalDirection && 'flex-col items-end',
      )}
    >
      <span className="flex items-start">
        <span
          className={priceIntegral({
            size,
            color: customColor ? undefined : color,
            fontWeight,
            lineThrough,
          })}
          style={customColor ? { color: customColor } : undefined}
        >
          {formatPriceForDisplay(finalPrice)}
        </span>
        <span
          className={priceCurrency({
            size,
            color: customColor ? undefined : color,
            fontWeight,
            lineThrough,
          })}
          style={customColor ? { color: customColor } : undefined}
        >
          {currency}
        </span>
      </span>
      {showVatSuffix ? (
        <VatPriceSuffix
          size={effectiveVatSize}
          customColor={vatSuffixColor}
          customBackground={vatSuffixBackground}
        />
      ) : null}
    </span>
  );
};

export default FormattedPrice;
